import { currentLocale } from './i18n';
import type {
  Asset,
  BackdropPreset,
  CameraPov,
  FashionModel,
  Garment,
  GarmentViewKind,
  GhostSide,
  FilmStock,
  GradePreset,
  LightingPreset,
  Pose,
  BriefPlan,
  ModelAppearance,
  ModelView,
  ReferenceRead,
  ModelViewKind,
  ModelWithStats,
  PipelineEvent,
  Place,
  PoseRef,
  Scene,
  SceneLook,
  ProviderStatus,
  Shoot,
  ShootPreset,
  ShootSettings,
  Shot,
} from '@studio/shared';

/**
 * On web and desktop the UI is served by the studio server itself, so relative
 * paths work. A phone running the native shell is a different device, so it
 * needs an absolute origin — baked in at build time via VITE_STUDIO_SERVER_URL.
 */
const ORIGIN = (import.meta.env.VITE_STUDIO_SERVER_URL ?? '').replace(/\/$/, '');
const BASE = `${ORIGIN}/api`;

/**
 * The CSRF token the server set as a readable cookie, echoed back in a header.
 *
 * Only same-origin script can read the cookie, so a cross-site page cannot
 * produce this header — which is what makes the pair a defence rather than
 * ceremony.
 */
function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)studio_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]!) : '';
}

/** An error that carries what the server called it, so callers can branch. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** Numbers behind `message`, for a client that says it in another language. */
    readonly vars?: Record<string, string | number>,
  ) {
    super(message);
  }
}

/**
 * Fired on `window` when the server answers 401 to a request that carried a
 * session: the session has expired or been revoked elsewhere. The session
 * provider listens and shows the sign-in page, so no page has to know.
 */
export const UNAUTHENTICATED_EVENT = 'studio:unauthenticated';

/*
 * The Studio catalogue, managed from the admin area. While it is open, the
 * requests that make models, places, poses and photographs are marked, and
 * the server carries them out as the catalogue (staff only) — so the model
 * editor, the uploads and the rest work on it unchanged.
 */
let catalogueMode = false;
const CATALOGUE_PATHS = /^\/(assets|models|places|poses|scenes)(\/|\?|$)/;
export const CATALOGUE_HEADER = { 'x-studio-catalogue': '1' };
export function setCatalogueMode(on: boolean): void {
  catalogueMode = on;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    // The session lives in a cookie, which is only sent if asked for.
    credentials: 'include',
    headers: {
      ...(init?.body instanceof FormData ? {} : { 'content-type': 'application/json' }),
      ...(init?.method && init.method !== 'GET' ? { 'x-csrf-token': csrfToken() } : {}),
      ...(catalogueMode && CATALOGUE_PATHS.test(path) ? CATALOGUE_HEADER : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      vars?: Record<string, string | number>;
    };
    // Not the sign-in attempts themselves: a wrong password is a 401 too,
    // and it should stay on the form that produced it.
    if (response.status === 401 && !path.startsWith('/login') && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    }
    throw new ApiError(
      body.error ?? `Request failed (${response.status})`,
      response.status,
      body.code,
      body.vars,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface Presets {
  maxFramesPerShoot: number;
  povs: CameraPov[];
  poses: Pose[];
  grades: GradePreset[];
  stocks: FilmStock[];
  lighting: LightingPreset[];
  backdrops: BackdropPreset[];
  aspectRatios: string[];
}

/** One slice of `/insights`: a preset, and how its frames fared. */
export interface InsightCut {
  id: string;
  label: string;
  frames: number;
  kept: number;
  rejected: number;
  downloads: number;
  keepRate?: number;
  meanScore?: number;
}

export interface Insights {
  frames: number;
  kept: number;
  rejected: number;
  downloads: number;
  unmarked: number;
  bySetUp: InsightCut[];
  byGrade: InsightCut[];
  byLight: InsightCut[];
  byScene: InsightCut[];
}

export interface ShootDetail {
  shoot: Shoot;
  shots: Shot[];
  running: boolean;
  events: PipelineEvent[];
  /** What the critic compared each frame against. */
  garments: Garment[];
  identityAssetIds: string[];
  /** What the roll has cost in provider calls so far, and where. */
  cost: SpendSummary & { byShot: Record<string, number> };
}

/** Micro-dollars: integers, so a month of tiny calls sums exactly. */
export interface SpendSummary {
  costMicros: number;
  calls: number;
  byStage: Record<string, number>;
  byModel: Record<string, number>;
}

export const usd = (micros: number, digits = 2): string => `$${(micros / 1_000_000).toFixed(digits)}`;

/** One message of a frame's conversation with Gemini, as it was sent, and its answer. */
export type StylistStep = 'outfit' | 'place' | 'pose';

/** One of the stylist's proposals, or the client's own idea made into one. */
export interface StylistOption {
  key: string;
  title: string;
  description: string;
  prompt?: string;
  /** A place or pose of the studio's, by id; `custom` for one in words. */
  ref?: string;
  /** Its photograph, for a photographed place or pose. */
  assetId?: string;
  /** A built-in place, for its example picture. */
  presetId?: string;
  completes?: boolean;
  own?: boolean;
}

export interface StylistVersion {
  shotId: string;
  shootId: string;
  n: number;
  chosen: Record<StylistStep, string>;
}

export interface StylistMessage {
  id: string;
  role: 'stylist' | 'client';
  kind: 'ask' | 'model' | 'pieces' | 'options' | 'choice' | 'version' | 'text' | 'error' | 'wait' | 'unclear';
  at: string;
  step?: string;
  modelId?: string;
  garmentIds?: string[];
  options?: StylistOption[];
  option?: StylistOption;
  shotId?: string;
  shootId?: string;
  n?: number;
  chosen?: Record<StylistStep, string>;
  /** Which take of the frame this message is about: 0 for the version itself, more for its corrections. */
  take?: number;
  correction?: string;
  text?: string;
}

export interface StylistFinishes {
  aperture: string;
  filmStockId: string;
  grain: number;
  vignette: number;
  fidelity: 'max' | 'standard';
}

export interface StylistConversation {
  id: string;
  locale: 'fr' | 'en';
  step: 'model' | 'pieces' | StylistStep | 'review';
  modelId?: string | null;
  garmentIds: string[];
  options: Partial<Record<StylistStep, StylistOption[]>>;
  chosen: Partial<Record<StylistStep, StylistOption>>;
  settings: StylistFinishes;
  shootId?: string | null;
  versions: StylistVersion[];
  messages: StylistMessage[];
  /** Each version's frame as it is now. */
  frames: Record<string, Shot>;
}

export interface StylistSummary {
  id: string;
  step: StylistConversation['step'];
  versions: number;
  chosen: Partial<Record<StylistStep, string | null>>;
  createdAt: string;
  updatedAt: string;
}

export interface SentStep {
  ask: {
    kind: string;
    text: string;
    images: Array<{
      assetId: string;
      label: string;
      grey?: boolean;
      exact?: boolean;
      veil?: number[][];
      /** A scene's woman: her bare skin, greyed with her head blurred. */
      skin?: number[][];
      /** A scene sent as it was: why its head was not blurred. */
      unveiled?: 'offline' | 'none';
    }>;
  };
  reply: Array<{ assetId?: string; text?: string; thought?: boolean }>;
}

export interface SentToGemini {
  steps: SentStep[];
  /** Messages Gemini would not draw for this frame, newest first. */
  refused: Array<{ at: string; model: string; reason: string; text: string; ask: SentStep['ask'] }>;
  calls: Array<{
    at: string;
    stage: string;
    model: string;
    attempt: number;
    inputTokens: number;
    outputTokens: number;
    costMicros: number;
    ms: number;
  }>;
}

export interface GalleryEntry {
  shot: Shot;
  asset?: Asset;
  shoot?: Shoot;
}

export interface DesignedModel extends ModelAppearance {
  name: string;
  identityPrompt: string;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
  accountId: string;
  emailVerified: boolean;
  locale?: string;
  theme?: string;
  /** Whether to offer the admin area. The server re-decides on every request. */
  staff?: boolean;
}

export interface AccountOverview {
  account?: { id: string; name: string; createdAt: string };
  user: SessionUser;
  members: Array<{ id: string; email: string; name: string; role: string; lastLoginAt?: string }>;
  /** Frames made this month. A count for the record — there is no cap. */
  usage: { period: string; frames: number };
}

export const auth = {
  me: () =>
    request<{
      user: SessionUser | null;
      signupOpen?: boolean;
      /** Signup is closed but open to a list of addresses. */
      signupByList?: boolean;
      authDisabled?: boolean;
      suspended?: boolean;
      /** Rendering waits for a confirmed address on this server. */
      verificationRequired?: boolean;
    }>('/me'),
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  /** Whether an invitation link can still be used, and the address it was sent to. */
  checkInvite: (token: string) =>
    request<{ valid: boolean; email?: string }>('/invite/check', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  /** Who runs this studio, for the terms and the privacy notice. */
  legal: () => request<{ operator: string; contact: string; host: string }>('/legal'),
  signup: (body: {
    email: string;
    password: string;
    name?: string;
    accountName?: string;
    invite?: string;
    acceptTerms: boolean;
  }) =>
    request<{ user?: SessionUser; ok?: boolean }>('/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  logout: () => request<{ ok: boolean }>('/logout', { method: 'POST' }),
  forgot: (email: string) =>
    request<{ ok: boolean }>('/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  reset: (token: string, password: string) =>
    request<{ user: SessionUser }>('/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  verify: (token: string) =>
    request<{ ok: boolean; user?: SessionUser }>('/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  resendVerification: () => request<{ ok: boolean; already?: boolean }>('/verify/resend', { method: 'POST' }),
  sessions: () =>
    request<
      Array<{
        id: string;
        createdAt: string;
        lastSeenAt: string;
        ip: string;
        userAgent: string;
        current: boolean;
      }>
    >('/sessions'),
  revokeSession: (id: string) => request<void>(`/sessions/${id}`, { method: 'DELETE' }),
};

/** What `/account/calibration` answers: the critic measured against the marks. */
export interface Calibration {
  sample: number;
  kept: number;
  rejected: number;
  keptMean?: number;
  rejectedMean?: number;
  currentAgreement?: number;
  falseRejects: number;
  falseAccepts: number;
  suggestedThreshold?: number;
  suggestedAgreement?: number;
  minimum: number;
}

export const account = {
  overview: () => request<AccountOverview>('/account'),
  update: (patch: { name?: string; accountName?: string; locale?: string; theme?: string }) =>
    request<{ user: SessionUser }>('/account', { method: 'PATCH', body: JSON.stringify(patch) }),
  changePassword: (current: string, next: string) =>
    request<{ ok: boolean }>('/account/password', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    }),
  activity: () =>
    request<Array<{ action: string; detail: string; ip: string; at: string }>>('/account/activity'),
  calibration: () => request<Calibration>('/account/calibration'),
  remove: (password: string, confirm: string) =>
    request<{ ok: boolean }>('/account/delete', {
      method: 'POST',
      body: JSON.stringify({ password, confirm }),
    }),
};

/* --------------------------------- admin -------------------------------- */

export interface AdminOverview {
  accounts: { total: number; new30: number; suspended: number };
  users: { total: number; active30: number };
  daily: Array<{ date: string; frames: number }>;
  work: {
    shoots: number;
    frames: number;
    framesThisPeriod: number;
    failed: number;
    models: number;
    storageBytes: number;
  };
  spend: {
    costMicros: number;
    frames: number;
    perFrameMicros: number;
    byStage: Record<string, number>;
    byModel: Record<string, number>;
  };
  period: string;
  staff: string[];
}

export interface AdminAccount {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  users: number;
  frames: number;
  models: number;
  shoots: number;
  /** Provider spend this period, in micro-dollars. */
  spendMicros: number;
}

export interface AdminAccountDetail {
  account: AdminAccount;
  spend: SpendSummary;
  users: Array<{ id: string; email: string; name: string; role: string; lastLoginAt?: string }>;
  usage: Array<{ period: string; metric: string; count: number }>;
  activity: Array<{ action: string; detail: string; at: string; ip: string }>;
  work: { shoots: number; frames: number; models: number; storageBytes: number };
}

export interface AdminInvite {
  id: string;
  email: string;
  note: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  usedAt?: string;
  usedByEmail?: string;
  revokedAt?: string;
  status: 'pending' | 'used' | 'expired' | 'revoked';
}

export const admin = {
  overview: () => request<AdminOverview>('/admin/overview'),
  invites: () => request<AdminInvite[]>('/admin/invites'),
  /** The link is in this answer only: the server keeps a digest of it. */
  createInvite: (body: { email?: string; note?: string; expiresInDays?: number }) =>
    request<{ invite: AdminInvite; url: string; emailed: boolean }>('/admin/invites', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revokeInvite: (id: string) => request<AdminInvite>(`/admin/invites/${id}/revoke`, { method: 'POST' }),
  accounts: (query = '', offset = 0) =>
    request<{ total: number; accounts: AdminAccount[] }>(
      `/admin/accounts?q=${encodeURIComponent(query)}&offset=${offset}`,
    ),
  account: (id: string) => request<AdminAccountDetail>(`/admin/accounts/${id}`),
  setStatus: (id: string, status: 'active' | 'suspended') =>
    request<{ ok: boolean }>(`/admin/accounts/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  removeAccount: (id: string, confirm: string) =>
    request<{ ok: boolean }>(`/admin/accounts/${id}/delete`, {
      method: 'POST',
      body: JSON.stringify({ confirm }),
    }),
};

export const api = {
  status: () => request<ProviderStatus>('/status'),
  presets: () => request<Presets>('/presets'),

  /**
   * `consent` is the uploader's attestation that they have the right to use
   * the likeness. The server requires it on a face and records it; nothing
   * else takes it.
   */
  uploadAsset(
    file: File,
    kind: Asset['kind'],
    options?: { consent?: boolean; view?: ModelViewKind },
  ): Promise<Asset> {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    if (options?.consent) form.append('consent', 'true');
    // A turnaround photograph is screened as a whole figure, from its side.
    if (options?.view) form.append('view', options.view);
    return request<Asset>('/assets', { method: 'POST', body: form });
  },

  /**
   * Imports an image from a web address: a photograph to recreate, a place,
   * a garment. A page works too — its preview image is taken.
   */
  importAsset: (url: string, kind: 'reference' | 'backdrop' | 'garment') =>
    request<Asset>('/assets/from-url', { method: 'POST', body: JSON.stringify({ url, kind }) }),
  /** What the studio sees in a photograph to recreate: pose, framing, place, light. */
  readReference: (assetId: string) =>
    request<ReferenceRead>('/references/read', { method: 'POST', body: JSON.stringify({ assetId }) }),
  /** A new version of a roll planned from a brief, from what was said about this one. */
  reviseShoot: (id: string, feedback: string, locale: 'fr' | 'en') =>
    request<{ shoot: Shoot; plan: BriefPlan }>(`/shoots/${id}/revise`, {
      method: 'POST',
      body: JSON.stringify({ feedback, locale }),
    }),

  listModels: () => request<ModelWithStats[]>('/models'),

  /** The places a roll is chosen from: the Studio's first, then the account's own. */
  listPlaces: () => request<Place[]>('/places'),
  createPlace: (body: { name: string; description: string; assetId?: string }) =>
    request<Place>('/places', { method: 'POST', body: JSON.stringify(body) }),
  updatePlace: (id: string, patch: { name?: string; description?: string; assetId?: string | null }) =>
    request<Place>(`/places/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deletePlace: (id: string) => request<void>(`/places/${id}`, { method: 'DELETE' }),

  /** Poses as photographs: added as they are, nothing is read. */
  listPoses: () => request<PoseRef[]>('/poses'),
  /** The Studio's scenes, and the account's own. */
  listScenes: () => request<Scene[]>('/scenes'),
  /** The stylist's three ways to wear these pieces in this scene. */
  sceneLooks: (sceneId: string, garmentIds: string[], locale: string) =>
    request<{ looks: SceneLook[] }>(`/scenes/${sceneId}/looks`, {
      method: 'POST',
      body: JSON.stringify({ garmentIds, locale }),
    }),
  createScene: (body: { assetId: string; name?: string }) =>
    request<Scene>('/scenes', { method: 'POST', body: JSON.stringify(body) }),
  renameScene: (id: string, name: string) =>
    request<Scene>(`/scenes/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteScene: (id: string) => request<void>(`/scenes/${id}`, { method: 'DELETE' }),
  /** The stylist: a shoot made in a conversation. */
  stylist: {
    recent: () => request<StylistSummary[]>('/stylist'),
    start: (locale: 'fr' | 'en') =>
      request<StylistConversation>('/stylist', { method: 'POST', body: JSON.stringify({ locale }) }),
    get: (id: string) => request<StylistConversation>(`/stylist/${id}`),
    remove: (id: string) => request<void>(`/stylist/${id}`, { method: 'DELETE' }),
    model: (id: string, modelId: string) =>
      request<StylistConversation>(`/stylist/${id}/model`, {
        method: 'POST',
        body: JSON.stringify({ modelId }),
      }),
    pieces: (id: string, garmentIds: string[]) =>
      request<StylistConversation>(`/stylist/${id}/pieces`, {
        method: 'POST',
        body: JSON.stringify({ garmentIds }),
      }),
    choose: (id: string, step: StylistStep, pick: { key: string } | { other: string }) =>
      request<StylistConversation>(`/stylist/${id}/choose`, {
        method: 'POST',
        body: JSON.stringify({ step, ...pick }),
      }),
    more: (id: string, step: StylistStep) =>
      request<StylistConversation>(`/stylist/${id}/more`, { method: 'POST', body: JSON.stringify({ step }) }),
    say: (id: string, text: string) =>
      request<StylistConversation>(`/stylist/${id}/say`, { method: 'POST', body: JSON.stringify({ text }) }),
    finishes: (id: string, patch: Partial<StylistFinishes>) =>
      request<StylistConversation>(`/stylist/${id}/finishes`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
  },
  /** Staff: one of their own frames offered to every client as a scene. */
  sceneFromShot: (shotId: string, name: string) =>
    request<Scene>('/scenes/from-shot', { method: 'POST', body: JSON.stringify({ shotId, name }) }),
  createPose: (body: { assetId: string; name?: string }) =>
    request<PoseRef>('/poses', { method: 'POST', body: JSON.stringify(body) }),
  renamePose: (id: string, name: string) =>
    request<PoseRef>(`/poses/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deletePose: (id: string) => request<void>(`/poses/${id}`, { method: 'DELETE' }),

  /** The Studio catalogue's own lists, asked for as the catalogue whatever the mode. */
  catalogue: {
    models: () => request<ModelWithStats[]>('/models', { headers: CATALOGUE_HEADER }),
    places: () => request<Place[]>('/places', { headers: CATALOGUE_HEADER }),
    poses: () => request<PoseRef[]>('/poses', { headers: CATALOGUE_HEADER }),
    scenes: () => request<Scene[]>('/scenes', { headers: CATALOGUE_HEADER }),
  },
  createModel: (body: {
    name: string;
    appearance: ModelAppearance;
    identityPrompt?: string;
    referenceAssetIds?: string[];
    views?: ModelView[];
    notes?: string;
    source?: FashionModel['source'];
  }) => request<FashionModel>('/models', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Draws the missing sides of a saved model's turnaround from her portraits
   * — or the sides named. Each one is a render.
   */
  generateViews: (modelId: string, kinds?: ModelViewKind[]) =>
    request<{ model: FashionModel; rendered: ModelView[]; error?: string }>(
      `/models/${modelId}/views/generate`,
      { method: 'POST', body: JSON.stringify(kinds ? { kinds } : {}) },
    ),
  /**
   * Draws a whole turnaround — front, profile and back, or the sides named —
   * for a face that is not saved as a model yet. Three renders.
   */
  drawViews: (identityPrompt: string, referenceAssetIds: string[], kinds?: ModelViewKind[]) =>
    request<{ views: ModelView[]; error?: string }>('/models/views', {
      method: 'POST',
      body: JSON.stringify({ identityPrompt, referenceAssetIds, ...(kinds ? { kinds } : {}) }),
    }),
  /**
   * Renders a reference portrait. Passing existing references makes it another
   * angle of that same person rather than a fresh stranger matching the words.
   */
  generatePortrait: (identityPrompt: string, referenceAssetIds: string[] = []) =>
    request<Asset>('/models/portrait', {
      method: 'POST',
      body: JSON.stringify({ identityPrompt, referenceAssetIds }),
    }),

  /**
   * Renders `count` fresh takes of a saved model's face. They arrive as
   * candidates — nothing about the model changes until one is picked.
   */
  shootPortraits: (modelId: string, count: number, fromReferences = true) =>
    request<{ model: FashionModel; assetIds: string[]; error?: string }>(`/models/${modelId}/portraits`, {
      method: 'POST',
      body: JSON.stringify({ count, fromReferences }),
    }),

  /** Accepts a take: it becomes the model's primary reference portrait. */
  keepPortrait: (modelId: string, assetId: string) =>
    request<FashionModel>(`/models/${modelId}/portraits/${assetId}/keep`, { method: 'POST' }),

  /** Throws a take away, file and all. */
  discardPortrait: (modelId: string, assetId: string) =>
    request<FashionModel>(`/models/${modelId}/portraits/${assetId}`, { method: 'DELETE' }),

  /** Reads a reference photograph and writes the model's description from it. */
  describeModel: (assetId: string) =>
    request<DesignedModel>('/models/describe', {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    }),
  designModel: (brief: string) =>
    request<DesignedModel>('/models/design', {
      method: 'POST',
      body: JSON.stringify({ brief }),
    }),
  updateModel: (id: string, patch: Partial<FashionModel>) =>
    request<FashionModel>(`/models/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteModel: (id: string) => request<void>(`/models/${id}`, { method: 'DELETE' }),

  listGarments: () => request<Garment[]>('/garments'),
  createGarment: (body: {
    assetId: string;
    label: string;
    category: Garment['category'];
    notes?: string;
    layer?: number;
  }) => request<Garment>('/garments', { method: 'POST', body: JSON.stringify(body) }),
  updateGarment: (id: string, patch: Partial<Garment>) =>
    request<Garment>(`/garments/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteGarment: (id: string) => request<void>(`/garments/${id}`, { method: 'DELETE' }),
  addGarmentView: (id: string, assetId: string, kind: GarmentViewKind, side?: 'left' | 'right') =>
    request<Garment>(`/garments/${id}/views`, {
      method: 'POST',
      body: JSON.stringify({ assetId, kind, ...(side ? { side } : {}) }),
    }),
  /** Ghost photographs of a piece, one per side photographed — a render each. */
  makeGarmentGhosts: (id: string, sides?: GhostSide[], retouch?: { correction?: string }) =>
    request<Garment>(`/garments/${id}/ghosts`, {
      method: 'POST',
      body: JSON.stringify({
        ...(sides ? { sides } : {}),
        ...(retouch
          ? { retouch: true, ...(retouch.correction ? { correction: retouch.correction } : {}) }
          : {}),
      }),
    }),
  removeGarmentView: (id: string, assetId: string) =>
    request<Garment>(`/garments/${id}/views/${assetId}`, { method: 'DELETE' }),
  /** Where on the piece a detail photograph is ("the zip", "the left cuff"). */
  noteGarmentView: (id: string, assetId: string, note: string) =>
    request<Garment>(`/garments/${id}/views/${assetId}`, { method: 'PATCH', body: JSON.stringify({ note }) }),

  /** Saved roll recipes: cast, angles and settings, without the garments. */
  listShootPresets: () => request<ShootPreset[]>('/shoot-presets'),
  createShootPreset: (body: {
    name: string;
    modelIds?: string[];
    povIds?: string[];
    settings: ShootSettings;
    fromShootId?: string;
  }) => request<ShootPreset>('/shoot-presets', { method: 'POST', body: JSON.stringify(body) }),
  updateShootPreset: (id: string, patch: Partial<Omit<ShootPreset, 'id' | 'createdAt' | 'updatedAt'>>) =>
    request<ShootPreset>(`/shoot-presets/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteShootPreset: (id: string) => request<void>(`/shoot-presets/${id}`, { method: 'DELETE' }),

  listShoots: () =>
    request<
      Array<
        Shoot & {
          shotCount: number;
          doneCount: number;
          keeperCount: number;
          thumbnails: string[];
          running: boolean;
        }
      >
    >('/shoots'),
  getShoot: (id: string) => request<ShootDetail>(`/shoots/${id}`),
  createShoot: (body: {
    name: string;
    modelIds: string[];
    garmentIds: string[];
    povIds: string[];
    settings: ShootSettings;
    start?: boolean;
  }) =>
    // The roll carries the language it was set up in, so its judges report in it.
    request<Shoot>('/shoots', {
      method: 'POST',
      body: JSON.stringify({ ...body, settings: { ...body.settings, locale: currentLocale() } }),
    }),
  startShoot: (id: string) => request<{ ok: boolean }>(`/shoots/${id}/start`, { method: 'POST' }),
  cancelShoot: (id: string) => request<{ ok: boolean }>(`/shoots/${id}/cancel`, { method: 'POST' }),
  deleteShoot: (id: string) => request<void>(`/shoots/${id}`, { method: 'DELETE' }),

  gallery: (mark?: 'keep') => request<GalleryEntry[]>(`/gallery${mark ? `?mark=${mark}` : ''}`),
  /** What this studio keeps, by set-up, grade, light and scene. */
  insights: () => request<Insights>('/insights'),

  /**
   * One frame again. With a correction written, the version `from` (the one
   * on show when left out) is edited — that change and nothing else; with
   * nothing written, it is drawn again from the references. `corrections` is
   * the earlier ones to keep; all of them when left out.
   */
  reshoot: (shotId: string, correction: string, corrections?: string[], from?: string) =>
    request<Shot>(`/shots/${shotId}/reshoot`, {
      method: 'POST',
      body: JSON.stringify({ correction, corrections, ...(from ? { from } : {}) }),
    }),

  /**
   * A scene's photograph, another variation of it: the next photograph a step
   * away, drawn again from the scene's edit the version `from` (the one on
   * show when left out) came from. One render.
   */
  vary: (shotId: string, from?: string) =>
    request<Shot>(`/shots/${shotId}/vary`, { method: 'POST', body: JSON.stringify(from ? { from } : {}) }),

  /**
   * Approves a roll's reference photograph: her face and the clothes are
   * right. The other poses in its place are then drawn from it.
   */
  approveShot: (shotId: string) => request<Shot>(`/shots/${shotId}/approve`, { method: 'POST' }),

  /** Puts an earlier picture of a frame back, from its history. Nothing is rendered. */
  restoreTake: (shotId: string, assetId: string) =>
    request<Shot>(`/shots/${shotId}/restore`, {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    }),

  /** Re-develops one frame at a different stock or grain, from its negative. */
  /** Staff: what Gemini was sent for a version of a frame, and what it answered. */
  sentToGemini: (shotId: string, assetId?: string) =>
    request<SentToGemini>(`/shots/${shotId}/sent${assetId ? `?asset=${encodeURIComponent(assetId)}` : ''}`),
  develop: (shotId: string, filmStockId: string, grain: number, vignette = 0) =>
    request<Shot>(`/shots/${shotId}/develop`, {
      method: 'POST',
      body: JSON.stringify({ filmStockId, grain, vignette }),
    }),

  /** Circle or cross a frame; null clears the mark. */
  markShot: (id: string, mark: 'keep' | 'reject' | null) =>
    request<Shot>(`/shots/${id}`, { method: 'PATCH', body: JSON.stringify({ mark }) }),
};

/** Subscribes to a shoot's progress. Returns an unsubscribe function. */
export function subscribeToShoot(shootId: string, onEvent: (event: PipelineEvent) => void): () => void {
  const source = new EventSource(`${BASE}/shoots/${shootId}/events`);
  source.onmessage = (message) => {
    try {
      onEvent(JSON.parse(message.data) as PipelineEvent);
    } catch {
      // A malformed frame shouldn't tear down the stream.
    }
  };
  return () => source.close();
}

/** A developed test strip for one stock at one strength. Pure, so it caches. */
export const stockSampleUrl = (stockId: string, grain: number, width = 200) =>
  `${BASE}/stocks/${stockId}/sample.png?grain=${Math.round(grain)}&w=${width}`;

export const assetUrl = (assetId: string) => `${BASE}/assets/${assetId}/raw`;
/** Staff: a scene's photograph as Gemini will be sent it, her head blurred and her bare skin greyed. */
export const sceneSentUrl = (sceneId: string) => `${BASE}/scenes/${sceneId}/sent`;
/** A reference as the renderer was sent it: smaller, and greyed, or with heads blurred and skin greyed, when it was. */
export const sentUrl = (image: { assetId: string; grey?: boolean; veil?: number[][]; skin?: number[][] }) => {
  const query = new URLSearchParams();
  if (image.grey) query.set('grey', 'true');
  if (image.veil?.length) query.set('veil', JSON.stringify(image.veil));
  if (image.skin?.length) query.set('skin', JSON.stringify(image.skin));
  return `${BASE}/assets/${image.assetId}/sent?${query.toString()}`;
};
/**
 * Where a frame is downloaded from. `finish` asks the server to resample and
 * sharpen on the way out — `2x` for a catalogue or a print, `sharp` to put
 * back the acutance without changing the size.
 */
export const downloadUrl = (assetId: string, finish?: '2x' | 'sharp') =>
  `${BASE}/assets/${assetId}/download${finish ? `?finish=${finish}` : ''}`;
